import { useEffect } from 'react';

const HEIGHT_VAR = '--visual-viewport-height';
const INSET_BOTTOM_VAR = '--visual-viewport-inset-bottom';

/**
 * Mirrors the visual viewport into CSS variables on `<html>`:
 * `--visual-viewport-height` is its height, `--visual-viewport-inset-bottom` the gap between its
 * bottom edge and the layout viewport's — i.e. how tall the software keyboard currently is.
 *
 * iOS Safari's `svh`/`lvh`/`dvh` all track the *layout* viewport, which the keyboard never shrinks,
 * and a `position: fixed; bottom: 0` element anchors to that same layout viewport. So a sheet sized
 * and pinned in those terms keeps its footer underneath the keyboard. Size off
 * `var(--visual-viewport-height, ...)` and offset off `var(--visual-viewport-inset-bottom, 0px)`
 * instead, always with a fallback for the first paint and for browsers without the API.
 *
 * iOS 26 shrinks `innerHeight` by however far it pans onto a focused input, yet still anchors
 * `bottom:` to the full-height box `clientHeight` reports — so the layout bottom is the larger of the two.
 */
export const useVisualViewportHeight = () => {
  useEffect(() => {
    const { visualViewport } = window;
    if (!visualViewport) return;

    const sync = () => {
      const { style } = document.documentElement;
      style.setProperty(HEIGHT_VAR, `${visualViewport.height}px`);
      const layoutHeight = Math.max(window.innerHeight, document.documentElement.clientHeight);
      const insetBottom = layoutHeight - visualViewport.height - visualViewport.offsetTop;
      style.setProperty(INSET_BOTTOM_VAR, `${Math.max(insetBottom, 0)}px`);
    };

    sync();
    visualViewport.addEventListener('resize', sync);
    // `offsetTop` moves without a resize as iOS pans the visual viewport onto a focused input.
    visualViewport.addEventListener('scroll', sync);
    return () => {
      visualViewport.removeEventListener('resize', sync);
      visualViewport.removeEventListener('scroll', sync);
    };
  }, []);
};
